import type { ReactElement } from "react";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { unlockDialog } from "./tenant-users.js";
import { adminCenterApi } from "./urls.js";

/** 今ロックされている人 1 人（サーバの LockedUserRow と同じ欄）。 */
export interface LockedUserRow {
    /** メールアドレス（ログイン ID）。解除の口へはこれを送る。 */
    readonly email: string;
    /** 表示名。どの契約にも居なければ null。 */
    readonly userName: string | null;
    /** ロックの始まりと終わり（日本時間の yyyy/MM/dd HH:mm。API が作る）。 */
    readonly lockedSince: string;
    readonly lockedUntil: string;
}

/** 今ロックされている人の一覧（<c>GET /api/admin-center/lockout</c> の lockedUsers ほか）。 */
export interface LockedUserList {
    /** ロックの始まりが新しい順（API が並べる）。 */
    readonly lockedUsers: readonly LockedUserRow[];
    /** 並べる人数の上限。 */
    readonly lockedUsersLimit: number;
    /** 上限を超えて並べきれない人が居るか。 */
    readonly lockedUsersFilled: boolean;
}

/**
 * 解除する。<b>口は契約 1 件の画面と同じ</b>（契約 ID を添えないので、終わるとロックの解除の画面へ戻る）。
 * 確認ダイアログの文も同じ（<c>unlockDialog</c>）。
 */
export function UnlockForm({ email, className }: {
    readonly email: string;
    readonly className: string;
}): ReactElement {
    const form = useApiForm(adminCenterApi.unlock, undefined, unlockDialog(email));

    return (
        <form method="post" onSubmit={form.onSubmit}>
            <FormSummary messages={form.summary} />
            <input type="hidden" name="email" value={email} />
            <button type="submit" className={className} disabled={form.busy}>ロックを解除する</button>
        </form>
    );
}

/** 上限を超えたときの断り。<b>黙って切ると「これで全部」と読める。</b> */
export const lockedUsersFilledMessage = (limit: number): string =>
    `${String(limit)} 人を超えています。ロックの始まりが新しい ${String(limit)} 人だけを出しています。ほかの人はメールアドレスで調べてください。`;

/**
 * 今ロックされている人を、ロックの始まりが新しい順に並べる。行ごとに解除できる。
 * <b>並べるのは今ロックされている人だけ</b>（登録されている人全体は並べない）。
 */
export function LockedUsers({ list }: { readonly list: LockedUserList }): ReactElement {
    const rows = list.lockedUsers;

    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">今ロックされている人（{rows.length}）</h2>
            <p className="mb-4 text-sm text-slate-500">ロックの始まりが新しい順に並べます。</p>

            {rows.length === 0
                ? <div className="empty-state text-sm text-slate-500">今ロックされている人はいません。</div>
                : (
                    <div className="table-card">
                        <div className="table-scroll">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th scope="col">メールアドレス</th>
                                        <th scope="col">名前</th>
                                        <th scope="col">ロックの始まり</th>
                                        <th scope="col">ロックの終わり</th>
                                        <th scope="col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.email}>
                                            <td className="font-medium text-ink-800">{row.email}</td>
                                            <td>{row.userName}</td>
                                            <td>{row.lockedSince}</td>
                                            <td>{row.lockedUntil}</td>
                                            <td className="cell-actions">
                                                <UnlockForm email={row.email} className="btn-secondary btn-sm" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            {list.lockedUsersFilled
                ? <p className="alert-warning mt-4">{lockedUsersFilledMessage(list.lockedUsersLimit)}</p>
                : null}
        </section>
    );
}
